import type { BetriebId } from '@/lib/betriebe';

export interface AktePhase {
  id: string;
  label: string;
}

export const AKTE_PHASEN: Record<BetriebId, AktePhase[]> = {
  gutachten: [
    { id: 'auftrag',      label: 'Auftrag' },
    { id: 'besichtigung', label: 'Besichtigung' },
    { id: 'gutachten',    label: 'Gutachten' },
    { id: 'versand',      label: 'Versand' },
    { id: 'rechnung',     label: 'Rechnung' },
    { id: 'bezahlt',      label: 'Bezahlt' },
  ],
  autoglas: [
    { id: 'auftrag',       label: 'Auftrag' },
    { id: 'bestellung',    label: 'Bestellung' },
    { id: 'terminplanung', label: 'Termin' },
    { id: 'montage',       label: 'Montage' },
    { id: 'rechnung',      label: 'Rechnung' },
    { id: 'bezahlt',       label: 'Bezahlt' },
  ],
  mietwagen: [
    { id: 'anfrage',     label: 'Anfrage' },
    { id: 'mietvertrag', label: 'Mietvertrag' },
    { id: 'uebergabe',   label: 'Übergabe' },
    { id: 'laufzeit',    label: 'Laufzeit' },
    { id: 'rueckgabe',   label: 'Rückgabe' },
    { id: 'abrechnung',  label: 'Abrechnung' },
    { id: 'bezahlt',     label: 'Bezahlt' },
  ],
  werkstatt: [
    { id: 'annahme',      label: 'Annahme' },
    { id: 'demontage',    label: 'Demontage' },
    { id: 'vermessung',   label: 'Vermessung' },
    { id: 'reparatur',    label: 'Reparatur' },
    { id: 'endkontrolle', label: 'Endkontrolle' },
    { id: 'uebergabe',    label: 'Übergabe' },
    { id: 'rechnung',     label: 'Rechnung' },
    { id: 'bezahlt',      label: 'Bezahlt' },
  ],
  recht: [
    { id: 'eingang',     label: 'Eingang' },
    { id: 'mahnstufe_1', label: 'Mahnung 1' },
    { id: 'mahnstufe_2', label: 'Mahnung 2/3' },
    { id: 'inkasso',     label: 'Inkasso/Anwalt' },
    { id: 'klage',       label: 'Klage/MB' },
    { id: 'tituliert',   label: 'Tituliert' },
    { id: 'bezahlt',     label: 'Bezahlt' },
  ],
  protex: [
    { id: 'auftrag',   label: 'Auftrag' },
    { id: 'reinigung', label: 'Reinigung' },
    { id: 'rechnung',  label: 'Rechnung' },
    { id: 'bezahlt',   label: 'Bezahlt' },
  ],
  unfallgiganten: [
    { id: 'lead',         label: 'Lead' },
    { id: 'qualifiziert', label: 'Qualifiziert' },
    { id: 'vermittelt',   label: 'Vermittelt' },
    { id: 'abgeschlossen',label: 'Abgeschlossen' },
  ],
  unfallwiki: [
    { id: 'recherche',  label: 'Recherche' },
    { id: 'redaktion',  label: 'Redaktion' },
    { id: 'publiziert', label: 'Publiziert' },
  ],
  airbnb: [
    { id: 'anfrage',  label: 'Anfrage' },
    { id: 'buchung',  label: 'Buchung' },
    { id: 'checkin',  label: 'Check-In' },
    { id: 'checkout', label: 'Check-Out' },
    { id: 'bezahlt',  label: 'Bezahlt' },
  ],
  app_programmierung: [
    { id: 'backlog',     label: 'Backlog' },
    { id: 'entwicklung', label: 'Entwicklung' },
    { id: 'review',      label: 'Review' },
    { id: 'live',        label: 'Live' },
  ],
};

// Maps old werkstatt phase IDs (stored in localStorage pre-PR #765) to current IDs.
const WERKSTATT_LEGACY_ID_MAP: Record<string, string> = {
  auftrag: 'annahme',
  kv: 'demontage',
};

// Maps old recht phase IDs (PIPELINE_PHASEN.recht had eingang/bearbeitung/abgeschlossen)
// to the expanded IDs introduced in PR #765.
const RECHT_LEGACY_ID_MAP: Record<string, string> = {
  bearbeitung: 'mahnstufe_1',
  abgeschlossen: 'bezahlt',
};

// airbnb / protex: only a new final phase 'bezahlt' was appended — all existing IDs still
// exist in the new config, so findIndex never returns -1 for stored records. No legacy map needed.

export function getAktePhasen(betriebId: string): AktePhase[] {
  return AKTE_PHASEN[betriebId as BetriebId] ?? AKTE_PHASEN.gutachten;
}

export function isGutachtenOnly(betriebId: string): boolean {
  return betriebId === 'gutachten';
}

/**
 * Resolves a stored phaseId to the canonical ID in the current AKTE_PHASEN definition.
 * Handles legacy IDs for werkstatt (PR #765) and recht (expanded from 3 to 7 phases in PR #765).
 */
export function resolvePhaseId(betriebId: string, storedPhaseId: string | undefined): string | undefined {
  if (!storedPhaseId) return storedPhaseId;
  if (betriebId === 'werkstatt') {
    const phasen = AKTE_PHASEN.werkstatt;
    if (!phasen.some(p => p.id === storedPhaseId)) {
      return WERKSTATT_LEGACY_ID_MAP[storedPhaseId] ?? storedPhaseId;
    }
  }
  if (betriebId === 'recht') {
    const phasen = AKTE_PHASEN.recht;
    if (!phasen.some(p => p.id === storedPhaseId)) {
      return RECHT_LEGACY_ID_MAP[storedPhaseId] ?? storedPhaseId;
    }
  }
  return storedPhaseId;
}
